import React, { useState } from 'react';
import { createMultipleBodyMeasurements } from '../services/bodyMeasurementsService';
import type { CreateBodyMeasurementRequest, DataSource } from '../types/bodyMeasurements';

interface BodyMeasurementSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (message: string, count: number) => void;
  onError?: (error: string) => void;
}

interface DeviceOption {
  id: string;
  name: string;
  manufacturer: string;
  icon: string;
  description: string;
  color: string;
}

export const BodyMeasurementSyncModal: React.FC<BodyMeasurementSyncModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onError
}) => {
  const [step, setStep] = useState<'select' | 'loading' | 'preview'>('select');
  const [syncedData, setSyncedData] = useState<CreateBodyMeasurementRequest[]>([]);
  const [loading, setLoading] = useState(false);

  const devices: DeviceOption[] = [
    {
      id: 'samsung_health',
      name: '삼성 헬스',
      manufacturer: 'Samsung',
      icon: '📱',
      description: '삼성 스마트폰 및 웨어러블 기기에서 데이터 로드',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'google_health_connect',
      name: '안드로이드 헬스',
      manufacturer: 'Google',
      icon: '🔗',
      description: 'Google Health Connect 및 Android 기기에서 데이터 로드',
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'ios_health',
      name: 'iPhone 헬스',
      manufacturer: 'Apple',
      icon: '🍎',
      description: 'iPhone 건강 앱에서 데이터 로드',
      color: 'from-gray-500 to-gray-600'
    }
  ];

  const handleSelectDevice = async (deviceId: string) => {
    setLoading(true);
    setStep('loading');

    try {
      // 실제 동기화 로직은 여기에 구현됩니다.
      // 현재는 시뮬레이션 데이터를 사용합니다.
      const simulatedData: CreateBodyMeasurementRequest[] = generateSimulatedData(deviceId);
      
      // 실제 환경에서는 여기서 기기별 API를 호출합니다
      // const data = await syncFromDevice(deviceId);
      
      setSyncedData(simulatedData);
      setStep('preview');
    } catch (error) {
      onError?.(error instanceof Error ? error.message : '데이터 동기화 실패');
      setStep('select');
    } finally {
      setLoading(false);
    }
  };

  const generateSimulatedData = (deviceId: string): CreateBodyMeasurementRequest[] => {
    // 실제 환경에서는 디바이스에서 가져온 실제 데이터를 사용합니다
    const dataSource: DataSource = deviceId === 'samsung_health' 
      ? 'samsung_health' 
      : deviceId === 'google_health_connect' 
      ? 'google_health_connect' 
      : 'samsung_health'; // iOS는 별도 처리 필요

    const now = new Date();
    const records: CreateBodyMeasurementRequest[] = [];

    // 시뮬레이션: 최근 7일간의 데이터
    for (let i = 0; i < 7; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      const weight = 70 + Math.random() * 2 - 1; // 70 ± 1kg
      const bodyFat = 25 + Math.random() * 2 - 1; // 25 ± 1%
      const bmi = Math.round((weight / 1.75) ** 2 * 10) / 10;

      records.push({
        measurement_time: new Date(date.getTime() - Math.random() * 3600000).toISOString(),
        weight: Math.round(weight * 10) / 10,
        body_fat_percentage: Math.round(bodyFat * 10) / 10,
        body_fat_mass: Math.round((weight * bodyFat / 100) * 10) / 10,
        skeletal_muscle_mass: Math.round((weight * 0.4) * 10) / 10,
        bmi,
        device_type: getDeviceTypeById(deviceId),
        device_manufacturer: getDeviceManufacturer(deviceId),
        data_source: dataSource as DataSource,
        notes: `${getDeviceTypeById(deviceId)}에서 동기화된 데이터`
      });
    }

    return records;
  };

  const getDeviceTypeById = (deviceId: string): string => {
    switch (deviceId) {
      case 'samsung_health':
        return 'Samsung Galaxy Watch 6';
      case 'google_health_connect':
        return 'Pixel Watch 2';
      case 'ios_health':
        return 'Apple Watch Series 9';
      default:
        return 'Unknown Device';
    }
  };

  const getDeviceManufacturer = (deviceId: string): string => {
    switch (deviceId) {
      case 'samsung_health':
        return 'Samsung';
      case 'google_health_connect':
        return 'Google';
      case 'ios_health':
        return 'Apple';
      default:
        return 'Unknown';
    }
  };

  const handleConfirmSync = async () => {
    if (syncedData.length === 0) {
      onError?.('동기화할 데이터가 없습니다');
      return;
    }

    try {
      setLoading(true);
      const results = await createMultipleBodyMeasurements(syncedData);
      onSuccess?.(`${results.length}건의 신체정보가 동기화되었습니다`, results.length);
      onClose();
    } catch (error) {
      onError?.(error instanceof Error ? error.message : '데이터 동기화 실패');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">신체정보 동기화</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* 콘텐츠 */}
        <div className="p-6">
          {step === 'select' && (
            <div className="space-y-4">
              <p className="text-gray-600 mb-6">
                아래에서 사용 중인 디바이스를 선택하여 신체정보를 동기화하세요.
              </p>
              
              {devices.map(device => (
                <button
                  key={device.id}
                  onClick={() => handleSelectDevice(device.id)}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left"
                >
                  <div className="flex items-start gap-4">
                    <div className={`text-3xl bg-gradient-to-br ${device.color} p-3 rounded-lg text-white`}>
                      {device.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{device.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{device.description}</p>
                      <p className="text-xs text-gray-500 mt-2">제조사: {device.manufacturer}</p>
                    </div>
                    <span className="text-2xl">→</span>
                  </div>
                </button>
              ))}

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 <strong>팁:</strong> 각 디바이스의 앱이 설치되어 있어야 동기화가 가능합니다.
                </p>
              </div>
            </div>
          )}

          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mb-4"></div>
              <p className="text-gray-600 font-medium">데이터를 동기화 중입니다...</p>
              <p className="text-sm text-gray-500 mt-2">잠시 기다려주세요</p>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  ✅ {syncedData.length}건의 데이터를 찾았습니다
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-4">동기화할 데이터 미리보기</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {syncedData.map((data, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-gray-50 rounded border border-gray-200"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">
                            {new Date(data.measurement_time).toLocaleDateString('ko-KR', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {data.weight && `체중: ${data.weight}kg`}
                            {data.body_fat_percentage && ` • 체지방: ${data.body_fat_percentage}%`}
                            {data.bmi && ` • BMI: ${data.bmi}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setStep('select');
                    setSyncedData([]);
                  }}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  다시 선택
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSync}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '동기화 중...' : '동기화 진행'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BodyMeasurementSyncModal;
